import React, { useMemo, useCallback, useState, useEffect } from "react";

// Components
import ProductResults from "./Results/ProductResults";
import ArticleResults from "./Results/ArticleResults";
import SeriesResult from "./Results/SeriesResult";
import MediaResults from "./Results/MediaResults";
import PagesResult from "./Results/PagesResult";
import DocumentResult from "./Results/DocumentResult";

const QuickSearchResults = (props) => {
    const [initialTab, setInitialTab] = useState("");
    const [trigger, setTrigger] = useState("");

    // Calculate Totals
    const calculateTotalPages = useCallback((totalItems) => {
        return Math.ceil(totalItems / 12);
    }, []);

    const totalArticlePages = useMemo(
        () => calculateTotalPages(props.results.commerceResults?.totalArticles),
        [calculateTotalPages, props.results.commerceResults?.totalArticles]
    );
    const totalProductPages = useMemo(
        () => calculateTotalPages(props.results.commerceResults?.totalProducts),
        [calculateTotalPages, props.results.commerceResults?.totalProducts]
    );
    const totalSeriesPages = useMemo(
        () => calculateTotalPages(props.results.commerceResults?.totalSeries),
        [calculateTotalPages, props.results.commerceResults?.totalSeries]
    );
    const totalPagePages = useMemo(
        () => calculateTotalPages(props.results.contentResults?.totalPages),
        [calculateTotalPages, props.results.contentResults?.totalPages]
    );
    const totalMediaPages = useMemo(
        () => calculateTotalPages(props.results.contentResults?.totalMedia),
        [calculateTotalPages, props.results.contentResults?.totalMedia]
    );
    const totalDocumentsPages = useMemo(
        () => calculateTotalPages(props.results.contentResults?.totalDocuments),
        [calculateTotalPages, props.results.contentResults?.totalDocuments]
    );

    // Use effects to determine the active tab
    useEffect(() => {
        const commerceResults = props.results.commerceResults;
        const contentResults = props.results.contentResults;

        // if (trigger === "" || trigger === "searchChange") {
        //     console.log("search was triggered by a search");
        // } else {
        //     console.log("search was triggered by a page change");
        // }

        if (props.results !== null && trigger !== "pageChange") {
            if (commerceResults.totalArticles > 0 || commerceResults.totalReplacementArticles > 0) {
                setInitialTab("articles");
            } else if (commerceResults.totalProducts > 0) {
                setInitialTab("products");
            } else if (commerceResults.totalSeries > 0) {
                setInitialTab("series");
            } else if (contentResults.totalPages > 0) {
                setInitialTab("pages");
            } else if (contentResults.totalMedia > 0) {
                setInitialTab("media");
            } else if (contentResults.totalDocuments > 0) {
                setInitialTab("documents");
            }
            props.resetPaginations();
        } else {
            setTrigger("searchChange");
        }
    }, [props.results]);

    // Pagination
    const generatePagination = useCallback((totalPages) => {
        return Array.from({ length: totalPages > 0 ? totalPages : 1 }, (_, index) => index + 1);
    }, []);

    const articlePagination = useMemo(
        () => generatePagination(totalArticlePages),
        [generatePagination, totalArticlePages]
    );
    const productPagination = useMemo(
        () => generatePagination(totalProductPages),
        [generatePagination, totalProductPages]
    );
    const seriesPagination = useMemo(
        () => generatePagination(totalSeriesPages),
        [generatePagination, totalSeriesPages]
    );
    const pagesPagination = useMemo(() => generatePagination(totalPagePages), [generatePagination, totalPagePages]);
    const mediaPagination = useMemo(() => generatePagination(totalMediaPages), [generatePagination, totalMediaPages]);
    const documentsPagination = useMemo(
        () => generatePagination(totalDocumentsPages),
        [generatePagination, totalDocumentsPages]
    );

    // Handle page change
    const handlePageChange = useCallback((page, setPage) => {
        setTrigger("pageChange");
        setPage(page);
    }, []);

    // Pipe to correct props function
    const pageChangeByType = useCallback(
        (pageType, page) => {
            switch (pageType) {
                case "articles":
                    handlePageChange(page, props.setArticlesPage);
                    break;
                case "products":
                    handlePageChange(page, props.setProductsPage);
                    break;
                case "series":
                    handlePageChange(page, props.setSeriesPage);
                    break;
                case "pages":
                    handlePageChange(page, props.setPagesPage);
                    break;
                case "media":
                    handlePageChange(page, props.setMediaPage);
                    break;
                case "documents":
                    handlePageChange(page, props.setDocumentsPage);
                    break;
                default:
                    break;
            }
        },
        [
            props.setArticlesPage,
            props.setProductsPage,
            props.setSeriesPage,
            props.setPagesPage,
            props.setMediaPage,
            props.setDocumentsPage,
        ]
    );

    let totalArticlesAndReplacement =
        props.results.commerceResults.totalArticles + props.results.commerceResults.totalReplacementArticles;

    return (
        <div>
            {props.results !== null && (
                <>
                    <ul className="nav nav-pills" id="pills-results-tab" role="tablist">
                        <li
                            className={"nav-item " + (totalArticlesAndReplacement < 1 ? "d-none" : "")}
                            role="presentation"
                        >
                            <button
                                onClick={() => setInitialTab("articles")}
                                className={"nav-link " + (initialTab === "articles" ? "active" : "")}
                                id="pills-articles-tab"
                                data-bs-toggle="pill"
                                data-bs-target="#pills-articles"
                                type="button"
                                role="tab"
                                aria-controls="pills-articles"
                                aria-selected="true"
                                disabled={totalArticlesAndReplacement < 1}
                            >
                                {i18n.getString("quicksearch.tabs.articles")} (
                                {props.results.commerceResults.totalArticles +
                                    props.results.commerceResults.totalReplacementArticles}
                                )
                            </button>
                        </li>
                        <li
                            className={"nav-item " + (props.results.commerceResults.totalProducts < 1 ? "d-none" : "")}
                            role="presentation"
                        >
                            <button
                                onClick={() => setInitialTab("products")}
                                className={"nav-link " + (initialTab === "products" ? "active" : "")}
                                id="pills-products-tab"
                                data-bs-toggle="pill"
                                data-bs-target="#pills-products"
                                type="button"
                                role="tab"
                                aria-controls="pills-products"
                                aria-selected="true"
                                disabled={props.results.commerceResults.totalProducts < 1}
                            >
                                {i18n.getString("quicksearch.tabs.products")} (
                                {props.results.commerceResults.totalProducts})
                            </button>
                        </li>
                        <li
                            className={"nav-item " + (props.results.commerceResults.totalSeries < 1 ? "d-none" : "")}
                            role="presentation"
                        >
                            <button
                                onClick={() => setInitialTab("series")}
                                className={"nav-link " + (initialTab === "series" ? "active" : "")}
                                id="pills-series-tab"
                                data-bs-toggle="pill"
                                data-bs-target="#pills-series"
                                type="button"
                                role="tab"
                                aria-controls="pills-series"
                                aria-selected="true"
                                disabled={props.results.commerceResults.totalSeries < 1}
                            >
                                {i18n.getString("quicksearch.tabs.series")} ({props.results.commerceResults.totalSeries}
                                )
                            </button>
                        </li>
                        <li
                            className={"nav-item " + (props.results.contentResults.totalPages < 1 ? "d-none" : "")}
                            role="presentation"
                        >
                            <button
                                onClick={() => setInitialTab("pages")}
                                className={"nav-link " + (initialTab === "pages" ? "active" : "")}
                                id="pills-pages-tab"
                                data-bs-toggle="pill"
                                data-bs-target="#pills-pages"
                                type="button"
                                role="tab"
                                aria-controls="pills-pages"
                                aria-selected="true"
                                disabled={props.results.contentResults.totalPages < 1}
                            >
                                {i18n.getString("quicksearch.tabs.pages")} ({props.results.contentResults.totalPages})
                            </button>
                        </li>
                        <li
                            className={"nav-item " + (props.results.contentResults.totalMedia < 1 ? "d-none" : "")}
                            role="presentation"
                        >
                            <button
                                onClick={() => setInitialTab("media")}
                                className={"nav-link " + (initialTab === "media" ? "active" : "")}
                                id="pills-media-tab"
                                data-bs-toggle="pill"
                                data-bs-target="#pills-media"
                                type="button"
                                role="tab"
                                aria-controls="pills-media"
                                aria-selected="true"
                                disabled={props.results.contentResults.totalMedia < 1}
                            >
                                {i18n.getString("quicksearch.tabs.media")} ({props.results.contentResults.totalMedia})
                            </button>
                        </li>
                        <li
                            className={"nav-item " + (props.results.contentResults.totalDocuments < 1 ? "d-none" : "")}
                            role="presentation"
                        >
                            <button
                                onClick={() => setInitialTab("documents")}
                                className={"nav-link " + (initialTab === "documents" ? "active" : "")}
                                id="pills-documents-tab"
                                data-bs-toggle="pill"
                                data-bs-target="#pills-documents"
                                type="button"
                                role="tab"
                                aria-controls="pills-documents"
                                aria-selected="true"
                                disabled={props.results.contentResults.totalDocuments < 1}
                            >
                                {i18n.getString("quicksearch.tabs.documents")} (
                                {props.results.contentResults.totalDocuments})
                            </button>
                        </li>
                    </ul>

                    <div className="tab-content" id="pills-tabResults">
                        <ArticleResults
                            initialTab={initialTab}
                            articles={props.results.commerceResults.articles}
                            query={props.query}
                            replacementArticles={props.results.commerceResults.replacementArticles}
                            pagination={articlePagination}
                            handleChange={(n) => pageChangeByType("articles", n)}
                            articlesPage={props.articlesPage}
                        />

                        <ProductResults
                            initialTab={initialTab}
                            products={props.results.commerceResults.products}
                            pagination={productPagination}
                            handleChange={(n) => pageChangeByType("products", n)}
                            productsPage={props.productsPage}
                        />

                        <SeriesResult
                            initialTab={initialTab}
                            series={props.results.commerceResults.series}
                            pagination={seriesPagination}
                            handleChange={(n) => pageChangeByType("series", n)}
                            seriesPage={props.seriesPage}
                        />

                        <PagesResult
                            initialTab={initialTab}
                            pages={props.results.contentResults.pages}
                            pagination={pagesPagination}
                            handleChange={(n) => pageChangeByType("pages", n)}
                            pagesPage={props.pagesPage}
                        />

                        <MediaResults
                            initialTab={initialTab}
                            media={props.results.contentResults.media}
                            pagination={mediaPagination}
                            handleChange={(n) => pageChangeByType("media", n)}
                            mediaPage={props.mediaPage}
                        />

                        <DocumentResult
                            initialTab={initialTab}
                            documents={props.results.contentResults.documents}
                            pagination={documentsPagination}
                            handleChange={(n) => pageChangeByType("documents", n)}
                            documentsPage={props.documentsPage}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default React.memo(QuickSearchResults);
